import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Skeleton } from "../components/ui/skeleton";
import axios from "axios";
import { formatDualCurrency, FX_CONFIG } from "../config/fx";
import { 
  Search, 
  Filter,
  X,
  Package,
  MapPin,
  Clock,
  ArrowRight
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "https://api.afrigulfmarket.com";

const Marketplace = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filters
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [moqRange, setMoqRange] = useState([0, 1000]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const getImageUrl = (paths) => {
    if (!paths || paths.length === 0) return null;
    const path = paths[0];
    if (!path) return null;
    return path.startsWith("http") ? path : `${API_URL}${path}`;
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (priceRange[0] > 0) params.append('min_price', priceRange[0]);
      if (priceRange[1] < 10000) params.append('max_price', priceRange[1]);
      
      const response = await axios.get(`${API_URL}/api/products?${params.toString()}`);
      setProducts(response.data);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data.categories);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const applyFilters = () => {
    const params = new URLSearchParams();
    if (selectedCategory) params.set('category', selectedCategory);
    setSearchParams(params);
    fetchProducts();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setPriceRange([0, 10000]);
    setMoqRange([0, 1000]);
    setSearchQuery('');
    setSearchParams({});
    fetchProducts();
  };

  const filteredProducts = products.filter(product => 
    product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="marketplace-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="container-main py-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('marketplace.title')}
          </h1>
          <p className="text-slate-500">
            Browse products from verified UAE suppliers
          </p>
        </div>
      </div>

      <div className="container-main py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar - Desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <Card className="card-base sticky top-24">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-semibold text-slate-900">{t('marketplace.filters')}</h3>
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    {t('marketplace.clearFilters')}
                  </Button>
                </div>

                {/* Category */}
                <div className="mb-6">
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    {t('marketplace.category')}
                  </label>
                  <Select value={selectedCategory || "all"} onValueChange={(v) => setSelectedCategory(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="category-filter" aria-label="Filter by category">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Price Range */}
                <div className="mb-6">
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    {t('marketplace.priceRange')}
                  </label>
                  <Slider
                    value={priceRange}
                    onValueChange={setPriceRange}
                    max={10000}
                    step={100}
                    className="mb-2"
                  />
                  <div className="flex justify-between text-sm text-slate-500">
                    <span>${priceRange[0]}</span>
                    <span>${priceRange[1]}+</span>
                  </div>
                </div>

                {/* MOQ Range */}
                <div className="mb-6">
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    {t('marketplace.moq')}
                  </label>
                  <Slider
                    value={moqRange}
                    onValueChange={setMoqRange}
                    max={1000}
                    step={10}
                    className="mb-2"
                  />
                  <div className="flex justify-between text-sm text-slate-500">
                    <span>{moqRange[0]} units</span>
                    <span>{moqRange[1]}+ units</span>
                  </div>
                </div>

                <Button onClick={applyFilters} className="w-full bg-[#0D4F8B] hover:bg-[#0A3D6B]" data-testid="apply-filters-btn">
                  {t('marketplace.applyFilters')}
                </Button>
              </CardContent>
            </Card>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Search & Mobile Filter */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder={t('common.search')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search products"
                  className="pl-10 input-style"
                  data-testid="search-input"
                />
              </div>
              <Button 
                variant="outline" 
                className="lg:hidden"
                onClick={() => setShowFilters(!showFilters)}
                aria-label="Toggle filters"
              >
                <Filter className="w-4 h-4 mr-2" />
                {t('common.filter')}
              </Button>
            </div>

            {/* Mobile Filters */}
            {showFilters && (
              <Card className="lg:hidden card-base mb-6">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold">{t('marketplace.filters')}</h3>
                    <Button variant="ghost" size="sm" onClick={() => setShowFilters(false)} aria-label="Close filters">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <Select value={selectedCategory || "all"} onValueChange={(v) => setSelectedCategory(v === "all" ? "" : v)}>
                    <SelectTrigger aria-label="Filter by category">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2 mt-4">
                    <Button onClick={clearFilters} variant="outline" className="flex-1">
                      {t('marketplace.clearFilters')}
                    </Button>
                    <Button onClick={applyFilters} className="flex-1 bg-[#0D4F8B]">
                      {t('marketplace.applyFilters')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Active Filters */}
            {selectedCategory && (
              <div className="flex items-center gap-2 mb-6">
                <Badge variant="secondary" className="flex items-center gap-1">
                  {selectedCategory}
                  <button onClick={() => { setSelectedCategory(''); fetchProducts(); }}>
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              </div>
            )}

            {/* Products Grid */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <Card key={i} className="card-base">
                    <CardContent className="p-0">
                      <Skeleton className="h-48 w-full" />
                      <div className="p-4 space-y-3">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-4 w-1/2" />
                        <Skeleton className="h-10 w-full" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-16">
                <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  {t('marketplace.noProducts')}
                </h3>
                <p className="text-slate-500 mb-6">
                  Try adjusting your filters or search query
                </p>
                <Button onClick={clearFilters} variant="outline">
                  {t('marketplace.clearFilters')}
                </Button>
              </div>
            ) : (
              <>
                <p className="text-sm text-slate-500 mb-4">{filteredProducts.length} products found</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredProducts.map((product) => (
                    <Card key={product.id} className="card-base card-interactive hover-lift overflow-hidden" data-testid={`product-${product.id}`}>
                      <CardContent className="p-0">
                        {/* Product Image */}
                        <div className="h-48 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                          {getImageUrl(product.image_paths) ? (
                            <img
                              src={getImageUrl(product.image_paths)}
                              alt={product.name}
                              className="h-full w-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <Package className="w-16 h-16 text-slate-300" />
                          )}
                        </div>
                        
                        {/* Product Info */}
                        <div className="p-4">
                          <Badge variant="outline" className="mb-2 text-xs">
                            {product.category}
                          </Badge>
                          <h3 className="font-semibold text-slate-900 mb-2 line-clamp-2">
                            {product.name}
                          </h3>
                          
                          {/* Price */}
                          <div className="mb-3">
                            <div className="text-lg font-bold text-[#0D4F8B]">
                              {formatDualCurrency(product.price_min_usd).usd} - {formatDualCurrency(product.price_max_usd).usd}
                            </div>
                            <div className="text-xs text-slate-500">
                              {formatDualCurrency(product.price_min_usd).aed} - {formatDualCurrency(product.price_max_usd).aed}
                            </div>
                          </div>
                          
                          {/* Meta */}
                          <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                            <div className="flex items-center gap-1">
                              <Package className="w-4 h-4" />
                              <span>MOQ: {product.moq}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              <span>{product.lead_time_days}d</span>
                            </div>
                          </div>
                          
                          {/* Supplier */}
                          {product.supplier_name && (
                            <div className="flex items-center gap-2 text-sm text-slate-500 mb-4 pb-4 border-b border-slate-100">
                              <MapPin className="w-4 h-4" />
                              <span>{product.supplier_name}</span>
                            </div>
                          )}
                          
                          {/* Actions */}
                          <div className="flex gap-2">
                            <Link to={`/marketplace/${product.id}`} className="flex-1">
                              <Button variant="outline" className="w-full text-sm">
                                View Details
                              </Button>
                            </Link>
                            <Link to={`/rfq?product=${product.id}`}>
                              <Button className="bg-[#10B981] hover:bg-[#059669] text-sm" data-testid={`quote-btn-${product.id}`}>
                                {t('marketplace.requestQuote')}
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}

            {/* FX Disclaimer */}
            <p className="text-xs text-slate-400 text-center mt-8">
              {FX_CONFIG.disclaimer}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Marketplace;


